import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { ArrowRight, FileText, Loader2, PlayCircle, X } from "lucide-react";
import { API, assetUrl, parseVideo } from "@/lib/api";
import { hubAccess } from "@/lib/hubAccess";

const media = "w-full h-full object-cover group-hover:scale-105 transition-transform duration-500";

export const CardMedia = ({ it, accent }) => {
  if (it.image) return <img src={assetUrl(it.image)} alt="" className={media} />;
  if (it.campaign_type !== "whitepaper") {
    const v = it.video_stream ? { kind: "file", src: `${API}/public/stream/${it.id}` } : it.video_url ? parseVideo(it.video_url) : null;
    if (v?.kind === "youtube") return <img data-testid={`hub-item-yt-thumb-${it.slug}`} src={`https://img.youtube.com/vi/${v.id}/hqdefault.jpg`} alt="" className={media} />;
    if (v?.kind === "file") return <video data-testid={`hub-item-video-preview-${it.slug}`} src={`${v.src}#t=2`} preload="metadata" muted playsInline className={`${media} pointer-events-none`} />;
  }
  return (
    <div className="w-full h-full grid place-items-center" style={{ background: `linear-gradient(135deg, ${accent}55, transparent)` }}>
      {it.campaign_type === "whitepaper" ? <FileText className="w-8 h-8 text-white/80" /> : <PlayCircle className="w-8 h-8 text-white/80" />}
    </div>
  );
};

const typeLabel = (it) => (it.campaign_type === "whitepaper" ? "White paper" : it.hub_only ? "Video" : "Webinar");

const SideItem = ({ it, active, onClick, accent, light, label }) => (
  <button type="button" data-testid={`hub-reader-item-${it.slug}`} onClick={onClick}
    className={`group w-full text-left rounded-xl border p-3 flex gap-3 items-center transition-colors ${active ? "" : "hover:border-current"} ${light ? "bg-white border-slate-200" : "bg-white/[0.04] border-white/10"}`}
    style={active ? { boxShadow: `inset 3px 0 0 ${accent}`, borderColor: accent } : undefined}>
    <div className="w-20 aspect-[16/10] rounded-md overflow-hidden bg-black/30 shrink-0"><CardMedia it={it} accent={accent} /></div>
    <div className="min-w-0">
      <p className="text-[10px] font-mono uppercase tracking-wider mb-1" style={{ color: accent }}>{label || typeLabel(it)}</p>
      <p className="text-sm font-semibold leading-snug line-clamp-2">{it.title}</p>
    </div>
  </button>
);

export const HubReader = ({ h, hubSlug }) => {
  const nav = useNavigate();
  const b = h.hub;
  const light = b.theme === "light";
  const accent = b.accent || "#EC4899";
  const dim = light ? "text-slate-500" : "text-slate-400";
  const items = useMemo(() => {
    const prim = h.items.find((i) => i.id === b.primary_id);
    return prim ? [prim, ...h.items.filter((i) => i !== prim)] : h.items;
  }, [h.items, b.primary_id]);
  const [sel, setSel] = useState(items[0]?.slug);
  const [ready, setReady] = useState("");
  const [failed, setFailed] = useState(false);

  const ensure = useCallback(async (slug) => {
    setReady("");
    setFailed(false);
    if (window.innerWidth < 1024) document.querySelector("[data-testid='hub-reader-main']")?.scrollIntoView({ behavior: "smooth" });
    const it = h.items.find((x) => x.slug === slug);
    try {
      if (await hubAccess(hubSlug, h.items, it)) return setReady(slug);
    } catch { return setFailed(true); }
    if (it.hub_only) return setFailed(true);
    nav(`/c/${slug}?hub=${hubSlug}&return=1`, { replace: true });
  }, [h.items, hubSlug]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => { if (sel) ensure(sel); }, [sel, ensure]);

  const [next, setNext] = useState(null);
  const prompted = useRef(new Set());
  useEffect(() => {
    const onMsg = (e) => {
      if (e.origin !== window.location.origin || e.data?.type !== "sv-asset-done" || prompted.current.has(e.data.slug)) return;
      prompted.current.add(e.data.slug);
      const i = items.findIndex((x) => x.slug === e.data.slug);
      const nx = [...items.slice(i + 1), ...items.slice(0, Math.max(i, 0))].find((x) => !prompted.current.has(x.slug));
      if (nx) setNext(nx);
    };
    window.addEventListener("message", onMsg);
    return () => window.removeEventListener("message", onMsg);
  }, [items]);
  const openNext = () => { setSel(next.slug); setNext(null); };
  useEffect(() => { setNext((n) => (n && n.slug === sel ? null : n)); }, [sel]);

  if (!items.length) return <p className={`max-w-6xl mx-auto px-6 py-20 ${dim}`}>New content is coming soon.</p>;
  const cur = items.find((i) => i.slug === sel) || items[0];

  return (
    <div className="max-w-[1500px] mx-auto px-5 py-6" data-testid="hub-reader">
      <header className="flex items-center gap-4 mb-6">
        {b.logo_path ? <img src={assetUrl(b.logo_path)} alt="Logo" className="h-9 w-auto object-contain" data-testid="hub-page-logo" /> : <span className="font-semibold">{h.client_name}</span>}
        <div className="min-w-0 flex-1">
          <h1 data-testid="hub-page-headline" className="text-xl sm:text-2xl font-bold tracking-tight truncate" style={{ fontFamily: `"${b.font_heading || "Outfit"}", sans-serif` }}>{b.headline || h.name}</h1>
          {b.message && <p data-testid="hub-page-message" className={`text-sm truncate ${dim}`}>{b.message}</p>}
        </div>
        <span className={`hidden sm:inline font-mono text-[11px] ${dim}`}>{h.code}</span>
      </header>
      <div className="grid lg:grid-cols-[320px_minmax(0,1fr)] gap-6">
        <aside className="order-2 lg:order-1 space-y-3 lg:sticky lg:top-6 self-start" data-testid="hub-reader-list">
          <p className={`font-mono text-[11px] uppercase tracking-[0.2em] ${dim}`}>Featured</p>
          <SideItem it={items[0]} active={cur.slug === items[0].slug} onClick={() => setSel(items[0].slug)} accent={accent} light={light} label={`Featured · ${typeLabel(items[0])}`} />
          {items.length > 1 && <p className={`font-mono text-[11px] uppercase tracking-[0.2em] pt-3 ${dim}`}>Further reading</p>}
          {items.slice(1).map((it) => (
            <SideItem key={it.slug} it={it} active={cur.slug === it.slug} onClick={() => setSel(it.slug)} accent={accent} light={light} />
          ))}
        </aside>
        <main data-testid="hub-reader-main" className={`relative order-1 lg:order-2 min-w-0 rounded-2xl border overflow-hidden ${light ? "border-slate-200 bg-white" : "border-white/10 bg-black/30"}`}>
          {failed ? <p className="p-10 text-center text-rose-400" data-testid="hub-reader-error">This content couldn't be opened. Please refresh the page.</p>
            : ready === cur.slug
              ? <iframe key={cur.slug} data-testid="hub-reader-frame" title={cur.title} src={`/c/${cur.slug}/${cur.campaign_type === "whitepaper" ? "read" : "watch"}?embed=1`}
                  className="w-full h-[calc(100vh-120px)] min-h-[560px] block" allow="autoplay; fullscreen; encrypted-media; picture-in-picture" allowFullScreen />
              : <div className="h-[60vh] grid place-items-center"><Loader2 className="w-6 h-6 animate-spin" style={{ color: accent }} /></div>}
          {next && (
            <div data-testid="hub-next-prompt" className={`absolute top-20 right-4 z-20 w-80 max-w-[calc(100%-2rem)] rounded-2xl border p-4 shadow-2xl sv-rise ${light ? "bg-white border-slate-200" : "bg-[#141A24] border-white/10"}`}>
              <button type="button" data-testid="hub-next-dismiss" onClick={() => setNext(null)} className={`absolute top-3 right-3 ${dim} hover:opacity-80`}><X className="w-4 h-4" /></button>
              <p className="font-mono text-[10px] uppercase tracking-[0.2em] mb-3" style={{ color: accent }}>Up next · {typeLabel(next)}</p>
              <div className="flex gap-3 items-center mb-4">
                <div className="w-20 aspect-[16/10] rounded-md overflow-hidden bg-black/30 shrink-0"><CardMedia it={next} accent={accent} /></div>
                <p data-testid="hub-next-title" className="text-sm font-semibold leading-snug line-clamp-3 pr-4">{next.title}</p>
              </div>
              <button type="button" data-testid="hub-next-open" onClick={openNext} className="h-9 px-4 rounded-full text-white text-sm font-medium inline-flex items-center gap-1.5 hover:opacity-90" style={{ background: accent }}>
                {next.campaign_type === "whitepaper" ? "Read now" : "Watch now"} <ArrowRight className="w-4 h-4" />
              </button>
            </div>
          )}
        </main>
      </div>
    </div>
  );
};
